import { FC } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps, PrismicRichText } from "@prismicio/react";

/**
 * Props for `SixColumnTable`.
 */
export type SixColumnTableProps =
  SliceComponentProps<Content.SixColumnTableSlice>;

/**
 * Component for "SixColumnTable" Slices.
 */
const SixColumnTable: FC<SixColumnTableProps> = ({ slice }) => {
  // Prepare rows arrays
  const rows: React.ReactNode[][] = Array.from({ length: 12 }, () => []);

  // Build cells
  slice.items.forEach((item: any, keyIndex: number) => {
    const hasRule = item.rule_below;

    // Determine cell background
    let cellColor = "";
    switch (item.cell_color) {
      case "green":
        cellColor = "bg-lightgrey";
        break;
      case "dark-green":
        cellColor = "bg-jade";
        break;
      case "orange":
        cellColor = "bg-orange/80";
        break;
      case "dark-orange":
        cellColor = "bg-orange";
        break;
      default:
        cellColor = "";
    }

    // Build content wrapper
    let proseTheme =
      "prose-p:text-jade prose-h4:text-[20px] prose-h4:font-semibold prose-h5:text-[20px] prose-h5:font-semibold";
    if (item.cell_color === "green") {
      proseTheme =
        "prose-p:text-jade prose-h3:text-lightgrey prose-p:font-medium prose-h3:text-[28px] prose-h3:font-normal prose-p:m-0 prose-p:p-0 prose-h3:m-0 prose-h3:p-0 prose-p:leading-[24px] prose-h4:text-[20px] prose-h4:font-semibold prose-h5:text-[20px] prose-h5:font-semibold";
    } else if (item.cell_color === "dark-green") {
      proseTheme =
        "prose-p:text-lightgrey prose-h3:text-lightgrey prose-h5:text-lightgrey prose-p:font-medium prose-h3:text-[28px] prose-h3:font-normal prose-p:m-0 prose-p:p-0 prose-h3:m-0 prose-h3:p-0 prose-p:leading-[24px] prose-h4:text-[20px] prose-h4:font-semibold prose-h5:text-[20px] prose-h5:font-semibold";
    } else if (item.cell_color === "orange") {
      proseTheme =
        "prose-p:text-lightgrey prose-h3:text-lightgrey prose-p:font-medium prose-h3:text-[28px] prose-h3:font-normal prose-p:m-0 prose-p:p-0 prose-h3:m-0 prose-h3:p-0 prose-p:leading-[24px] prose-h4:text-[20px] prose-h4:font-semibold prose-h5:text-[20px] prose-h5:font-semibold prose-h4:text-lightgrey prose-h5:text-lightgrey";
    } else if (item.cell_color === "dark-orange") {
      proseTheme =
        "prose-p:text-lightgrey prose-h3:text-lightgrey prose-p:font-medium prose-h3:text-[28px] prose-h3:font-normal prose-p:m-0 prose-p:p-0 prose-h3:m-0 prose-h3:p-0 prose-p:leading-[24px] prose-h4:text-[20px] prose-h4:font-semibold prose-h5:text-[20px] prose-h5:font-semibold prose-h5:text-lightgrey";
    }

    // Dot size
    let dotSize = "";
    switch (item.dot_size) {
      case "small":
        dotSize = "w-[14px] h-[14px] rounded-full mt-[84px] mb-[14px] bg-lightgrey";
        break;
      case "medium":
        dotSize = "w-[23px] h-[23px] rounded-full mt-[75px] mb-[14px] bg-lightgrey";
        break;
      case "large":
        dotSize = "w-[40px] h-[40px] rounded-full mt-[58px] mb-[14px] bg-orange/80";
        break;
      case "larger":
        dotSize = "w-[55px] h-[55px] bg-orange/80 mt-[43px] mb-[14px] rounded-full";
        break;
      case "x-large":
        dotSize = "w-[86px] h-[86px] bg-orange mt-[12px] mb-[14px] rounded-full";
        break;
      default:
        dotSize = "";
    }

    // Content node
    const content = (
      <div className="relative z-10 flex flex-col h-full pt-[21px] px-[20px] pb-[22px]">
        <div className={dotSize}></div>
        <article className={`prose ${proseTheme}`}>  
          <PrismicRichText field={item.col_content} />
        </article>
      </div>
    );

    // Determine row index (0-based)
    const colIdx = Number(item.column) - 1;
    if (colIdx >= 0 && colIdx < 12) {
      // Wrap in <td> with background
      const cell = (
        <td
          key={item.column + item.row + keyIndex}
          className={`p-0 w-[224px] min-w-[179px] align-top relative h-full ${cellColor} ${
            hasRule ? "border-b-[1px] border-b-jade" : ""
          }`}
        >
          {content}
        </td>
      );
      rows[colIdx].push(cell);
    }
  });

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="font-fellix text-[16px] mb-24"
    >
      <div className="six-column-table">
        <div className="max-w-[1280px] mx-auto">
          <PrismicRichText field={slice.primary.table_title} />
          <div className="overflow-x-auto w-full pb-4 mb-4">
            <table className="min-w-full table-auto border-collapse">
              <tbody>
                {rows.map((cells, idx) => (
                  <tr key={idx} className="p-0">
                    {cells}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <article className="text-[12px]">
            <PrismicRichText field={slice.primary.disclaimers} />
          </article>
        </div>
      </div>
    </section>
  );
};

export default SixColumnTable;
